import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { useSearchParams } from 'react-router'
import { AxiosError } from 'axios'
import { authService } from '@/services/api'
import { resetPasswordFormSchema, defaultResetPasswordValues } from './ResetPasswordForm.static'
import type { ResetPasswordFormValues } from './ResetPasswordForm.static'
import type { ApiError } from '@/types'

export function useResetPasswordForm() {
  const { t } = useTranslation('auth')
  const [searchParams] = useSearchParams()
  const [isSuccess, setIsSuccess] = useState(false)

  const token = searchParams.get('token')

  const form = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordFormSchema),
    defaultValues: defaultResetPasswordValues,
    mode: 'onBlur',
  })

  const resetPasswordMutation = useMutation({
    mutationFn: (data: { token: string; password: string }) => authService.resetPassword(data),
    onSuccess: () => {
      setIsSuccess(true)
    },
    onError: (error: AxiosError<ApiError>) => {
      const message =
        error.response?.status === 429
          ? t('errors.rateLimitExceeded')
          : t('errors.resetPasswordFailed')
      form.setError('root', {
        type: 'manual',
        message,
      })
    },
  })

  const onSubmit = (data: ResetPasswordFormValues) => {
    if (!token) return
    resetPasswordMutation.mutate({ token, password: data.password })
  }

  return {
    t,
    form,
    token,
    isLoading: resetPasswordMutation.isPending,
    isSuccess,
    onSubmit,
  }
}
